import { CompetitionLifterTable } from './components/competition-lifter-table';
import { RegisterLifterForm } from './components/register-lifter-form';
import { useCreateCompetitionRegistrationMutation } from './mutations';
import { Schema } from './components/register-lifter-form-schema';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useAuthentication } from '~/hooks/useAuthentication';
import { withPrivateRoute } from '~/hocs/withPrivateRoute';
import { useCompetitionQuery } from '../../queries';
import { Panel } from '~/components/Panel/Panel';
import { Link } from '~/components/Link/Link';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { idParamSchema } from '~/schemas';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

export const Page = withPrivateRoute(() => {
  const mutation = useCreateCompetitionRegistrationMutation();
  const { id } = useParams(idParamSchema.parse);
  const { data } = useCompetitionQuery(id);
  const { can } = useAuthentication();

  async function handleSubmit(payload: Schema) {
    await toast.promise(
      mutation.mutateAsync({
        competition_type: payload.competition_type_id ?? undefined,
        weight_class: payload.weight_class_id,
        age_category: payload.category_id,
        is_reserve: payload.is_reserve,
        person: payload.person_id,
        competition: id,
        team: payload.team_id,
      }),
      {
        success: 'Lyftaren har anmälts',
        pending: 'Anmäler lyftare...',
        error: {
          render({ data: error }) {
            if (
              isAxiosError<{ detail: string }>(error) &&
              error.response?.data.detail === 'Registration limit reached'
            ) {
              return 'Anmälningsgränsen är nådd';
            }
            return 'Lyftaren är redan anmäld';
          },
        },
      }
    );
  }

  if (!data) return false;

  const checkForClubAdmin = Boolean(
    data.open_externally && data.reg_deadline && new Date(data.reg_deadline) > new Date()
  );

  const isLimitReached = data.registrations_limit != null && data.registrations_count >= data.registrations_limit;

  return (
    <Fragment>
      <PageLayout.Header>
        <PageLayout.Heading text={data.name} />
      </PageLayout.Header>

      <PageLayout.SubHeading>Hantera anmälningar</PageLayout.SubHeading>

      {isLimitReached && (
        <Panel variant="error" size="sm" rounded>
          Den totala anmälningsgränsen för tävlingen är nådd — alla platser är fyllda.
          {can('edit-competition', id) && (
            <div style={{ marginTop: 'var(--size-2)' }}>
              Du kan ändra gränsen för antalet platser under{' '}
              <Link to={`/competitions/${id}/edit`} underline>
                Redigera tävling
              </Link>
              .
            </div>
          )}
        </Panel>
      )}

      <PageLayout.Section>
        {can('create-competition-registration', id, checkForClubAdmin) && (
          <RegisterLifterForm
            onSubmit={handleSubmit}
            isLoading={mutation.isPending}
            competitionId={id}
            isTeamComp={data.is_team_comp ?? false}
          />
        )}

        <CompetitionLifterTable
          competitionId={id}
          checkForClubAdmin={checkForClubAdmin}
          isTeamComp={data.is_team_comp ?? false}
        />
      </PageLayout.Section>
    </Fragment>
  );
});
